import React, { useState, useEffect, useCallback } from 'react';
import { useRoutes, usePath } from 'hooks/useHookRouterCompat';
import styled from 'styled-components';
import * as User from 'data/User';
import Vendor from 'data/Vendor';
import FeatureFlag, { FeatureFlagValues } from 'data/FeatureFlag';
import { grantAccess } from 'utils/authorization';
import SideNavigation from 'common/SideNavigation';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { useAPI } from 'lib/API';
import { useAuth } from 'lib/Auth';
import { Percent } from 'lib/styles';
import useBreadcrumbs from 'hooks/useBreadcrumbs';
import usePathState from 'hooks/usePathState';
import { loadUserPreferences } from 'requests/user';
import { fetchMetaData } from '../requests/meta';
import { getUserBusinessUnits } from '../requests/organization';
import { save } from '../lib/SessionStorage';
import { ROUTES_VIEWER_ROLE, STORAGE_KEYS } from 'constants/general';
import { BusinessUnit } from 'data/BusinessUnit';
import RootNavigation from './RootNavigation';
import { getUserPermissionsByRole } from 'requests/userPermissions';
import { GroupedUserPermissions } from 'data/UserPermissions';
import VendorBrandSelectionHeader from 'components/UI/VendorBrandSelectionHeader';
import { useAdminViewToggle } from 'hooks/useAdminViewToggle';
import { useHeaderVisibility } from 'hooks/useHeaderVisibility';
import { useVendorBusinessUnitPersistence } from 'hooks/useVendorBusinessUnitPersistence';
import createRoutes from 'utils/routes';
import { VendorBusinessUnitSelectionHandler, shouldDisableBusinessUnitSelector } from 'utils/vendorBusinessUnitSelectionHandler';
import { BusinessUnitProvider } from 'context/businessUnit';
import { VendorProvider } from 'context/vendor';
import ChatbotWidget from 'components/Chatbot/ChatbotWidget';

const PageLayout = styled.div`
  display: inline-flex;
  width: ${Percent.full};
`;

const MainPage = styled.div`
  width: ${Percent.full};
  display: flex;
  justify-content: center;
  overflow-x: auto;
  margin-left: 220px;
`;




const RootV2 = () => {
  const { user, token } = useAuth();
  const path = usePath();

  // Check if current page should have business unit selector disabled
  const shouldDisableBusinessUnitSelection = shouldDisableBusinessUnitSelector(path);

  const [userRoutes, setUserRoutes] = useState({});
  const [vendorsList, setVendorsList] = useState<Vendor[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [businessUnitList, setBusinessUnitList] = useState<BusinessUnit[]>([]);
  const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<BusinessUnit | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag>({});
  const [userPreferences, setUserPreferences] = useState<User.UserPreferences | null>(null);
  const crumbs = useBreadcrumbs(path);
  const { currentPath, previousPath } = usePathState();
  const [routeKey, setRouteKey] = useState(0);
  const [isMetaLoaded, setIsMetaLoaded] = useState(false);
  const [initialSelectionDone, setInitialSelectionDone] = useState(false);

  // Use custom hooks for persistence and logic separation
  const {
    persistVendorSelection,
    persistBusinessUnitSelection,
    restoreSelectionsFromStorage,
    clearStoredSelections
  } = useVendorBusinessUnitPersistence();


  // Header visibility logic
  const { isTopHeaderEnabled, shouldShowBreadcrumbs } = useHeaderVisibility({ currentPath: path });

  // Admin view toggle hook
  const {
    isAdminView,
    canToggleAdminView,
    handleAdminViewToggle,
    handleVendorSelection,
    handleBusinessUnitSelection
  } = useAdminViewToggle({
    user,
    vendorsList,
    businessUnitList,
    onVendorChange: (vendor) => {
      setSelectedVendor(vendor);
      // Only persist for OFMP admin users
      if (User.isOfmpAdmin(user!)) {
        persistVendorSelection(vendor);
      }
    },
    onBusinessUnitChange: (businessUnit) => {
      setSelectedBusinessUnit(businessUnit);
      // Only persist for OFMP admin users
      if (User.isOfmpAdmin(user!)) {
        persistBusinessUnitSelection(businessUnit);
      }
    }
  });

  const businessUnitsReq = useAPI({
    promiseFn: getUserBusinessUnits,
    onResolve: result => {
      if (result?.data) {
        const businessUnits = result.data;
        setBusinessUnitList(businessUnits);

        // Save to session storage
        save(STORAGE_KEYS.BUSINESS_UNITS, businessUnits);
      }
    },
    onReject: () => {
      renderToast(toast.TYPE.ERROR, 'Failed to load business units');
    }
  });

  const refetchBusinessUnits = useCallback(async () => {
    await businessUnitsReq.reload();
  }, [businessUnitsReq]);

  const metaDataReq = useAPI({
    promiseFn: fetchMetaData,
    onResolve: result => {
      if (result.meta) {
        const { brands, countries, productStatuses, vendors, countryStates, serviceStatuses, serviceTypes, unitOfMeasurements, roles, permissionGroups, rejectionReasons, commissionStatuses, commissionActions } = result.meta;

        // Save all metadata to session storage
        const metaDataToSave = {
          [STORAGE_KEYS.BRANDS]: brands,
          [STORAGE_KEYS.COUNTRIES]: countries,
          [STORAGE_KEYS.PRODUCT_STATUSES]: productStatuses,
          [STORAGE_KEYS.VENDORS]: vendors,
          [STORAGE_KEYS.COUNTRY_STATES]: countryStates,
          [STORAGE_KEYS.SERVICE_STATUSES]: serviceStatuses,
          [STORAGE_KEYS.SERVICE_TYPES]: serviceTypes,
          [STORAGE_KEYS.UNIT_OF_MEASUREMENTS]: unitOfMeasurements,
          [STORAGE_KEYS.ROLES]: roles,
          [STORAGE_KEYS.PERMISSION_GROUPS]: permissionGroups,
          [STORAGE_KEYS.REJECTION_REASONS]: rejectionReasons,
          [STORAGE_KEYS.COMMISSION_STATUSES]: commissionStatuses,
          [STORAGE_KEYS.COMMISSION_ACTIONS]: commissionActions,
        };

        Object.entries(metaDataToSave).forEach(([key, value]) => {
          if (value) save(key, value);
        });

        if (result.meta.featureFlags) {
          save(STORAGE_KEYS.FEATURE_FLAGS, result.meta.featureFlags);
          setFeatureFlags(result.meta.featureFlags);
        }

        // Set vendors
        setVendorsList(vendors);

        setIsMetaLoaded(true);
      }
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
      setIsMetaLoaded(false);
    }
  });

  const userPreferencesReq = useAPI({
    promiseFn: loadUserPreferences,
    onResolve: result => {
      setUserPreferences(result);
    },
    onReject: err => {
      renderToast(toast.TYPE.ERROR, err.message);
    }
  });

  // Handle vendor/business unit selection when both are loaded
  useEffect(() => {
    if (!initialSelectionDone && vendorsList.length > 0 && businessUnitList.length > 0 && user) {
      // Only attempt to restore selections for OFMP admin users
      const restoredSelections = User.isOfmpAdmin(user) ? restoreSelectionsFromStorage(vendorsList, businessUnitList) : null;
      const selectionResult = VendorBusinessUnitSelectionHandler.handleSelection({
        vendors: vendorsList,
        businessUnits: businessUnitList,
        user,
        isAdminView,
        restoredSelections
      });

      // Apply selections
      setSelectedVendor(selectionResult.vendor);
      setSelectedBusinessUnit(selectionResult.businessUnit);

      // Persist selections only for OFMP admin users and only if not in admin view
      if (!isAdminView && User.isOfmpAdmin(user) && (selectionResult.vendor || selectionResult.businessUnit)) {
        persistVendorSelection(selectionResult.vendor);
        persistBusinessUnitSelection(selectionResult.businessUnit);
      }

      setInitialSelectionDone(true);
    }
  }, [vendorsList, businessUnitList, initialSelectionDone, user, isAdminView, restoreSelectionsFromStorage, persistVendorSelection, persistBusinessUnitSelection]);

  useEffect(() => {
    const isVendor = User.isVendor(user!);
    const isBrandLevelRole = User.isBrandLevelRoles(user!);
    setUserRoutes(isVendor || isBrandLevelRole ? createRoutes(ROUTES_VIEWER_ROLE.EDITOR) : createRoutes(ROUTES_VIEWER_ROLE.ADMIN));
  }, [user]);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (selectedBusinessUnit?.roleId) {
        // Specific business unit selected - fetch and save permissions
        try {
          const response = await getUserPermissionsByRole(
            [selectedBusinessUnit.roleId],
            { token }
          );

          if (response && Array.isArray(response)) {
            const permissionsData = response as GroupedUserPermissions[];
            if (permissionsData) save(STORAGE_KEYS.PERMISSION_GROUPS, permissionsData);

            // Force re-rendering of routes by updating the key
            setRouteKey(prevKey => prevKey + 1);
          }
        } catch (error) {
          console.error('Error fetching permissions:', error);
        }
      }
    };

    fetchPermissions();
  }, [selectedBusinessUnit, selectedVendor, token]);

  // Clear persisted selections when admin view is enabled
  useEffect(() => {
    if (isAdminView && User.isOfmpAdmin(user!)) {
      clearStoredSelections();
    }
  }, [isAdminView, user, clearStoredSelections]);

  // Create wrapper functions to maintain the existing interface
  const updateVendor = useCallback((vendorName: string, brandId?: number) => {
    handleVendorSelection(vendorName, brandId);
  }, [handleVendorSelection]);

  const updateBusinessUnit = useCallback((businessUnitId: string | null) => {
    handleBusinessUnitSelection(businessUnitId);
  }, [handleBusinessUnitSelection]);

  let page = useRoutes(userRoutes);
  if (page !== null) {
    const props = {
      user,
      selectedVendor,
      updateVendor,
      updateBusinessUnit,
      vendorsList,
      featureFlags,
      userPreferences,
      userPreferencesReq,
      setVendorsList,
      selectedBusinessUnit
    };
    page = page(props);
  }

  if (metaDataReq.isLoading || !isMetaLoaded) {
    return <LoadingIndicator center />;
  }

  return (
    <VendorProvider selectedVendor={selectedVendor} updateVendor={updateVendor}>
      <BusinessUnitProvider
        businessUnitList={businessUnitList}
        selectedBusinessUnit={selectedBusinessUnit}
        updateBusinessUnit={updateBusinessUnit}
        refetchBusinessUnits={refetchBusinessUnits}
      >
        <PageLayout>
          <SideNavigation user={user!} featureFlags={featureFlags} />
          <MainPage key={routeKey}>
            <FlexboxContainer flexDirection="column" width="100%" padding="80px 0 0 0">
              <VendorBrandSelectionHeader
                user={user}
                vendorsList={vendorsList}
                selectedVendor={selectedVendor}
                updateVendor={updateVendor}
                selectedBusinessUnit={selectedBusinessUnit}
                updateBusinessUnit={updateBusinessUnit}
                disabled={!isTopHeaderEnabled}
                isAdminView={isAdminView}
                onAdminViewToggle={canToggleAdminView ? handleAdminViewToggle : undefined}
                disableBusinessUnitSelector={shouldDisableBusinessUnitSelection}
              />

              <RootNavigation
                currentPath={currentPath}
                previousPath={previousPath}
                showBreadcrumbs={shouldShowBreadcrumbs}
                crumbs={crumbs}
              />

              {page}
            </FlexboxContainer>
          </MainPage>
          {grantAccess(featureFlags, FeatureFlagValues.AI_CHATBOT, user!) && <ChatbotWidget />}
        </PageLayout>
      </BusinessUnitProvider>
    </VendorProvider>
  );
};

export default RootV2;
